// /communities/:slug/dashboard — the stats view. Pure analytics — no
// tabs, no editorial actions. Post-T3.1:
//   - Editorial queue → moved to Newsroom (T3.2, was a duplicate anyway)
//   - Editors panel → moved to Settings
//   - Boundary link → moved to Settings
// The page is intentionally lean now: Overview metrics + top posts.
// Actions live on their proper home pages.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useApi } from '../lib/api';
import { useAuth } from '../lib/auth';
import { timeAgo } from '../lib/format';

interface CommunityDashboardStats {
    community_id: string;
    community_slug: string;
    moderator_count: number;
    pending_count: number;
    approved_total: number;
    approved_30d: number;
    rejected_30d: number;
    total_likes: number;
    likes_30d: number;
    total_comments: number;
    comments_30d: number;
    open_flag_count: number;
    approved_per_week:   Array<{ week_start: string; count: number }>;
    likes_per_week: Array<{ week_start: string; count: number }>;
    top_posts: Array<{
        post_id: string; title: string; post_type: string;
        created_at: string; like_count: number; comment_count: number;
    }>;
    recent_moderators: Array<{
        user_id: string; display_name: string; role: string; granted_at: string;
    }>;
}

export function CommunityDashboard() {
    const { slug } = useParams<{ slug: string }>();
    const api = useApi();
    const { authed, login } = useAuth();
    const [stats, setStats] = useState<CommunityDashboardStats | null>(null);
    const [err,   setErr]   = useState('');

    const load = useCallback(async () => {
        if (!authed || !slug) return;
        try {
            setStats(await api.get<CommunityDashboardStats>('/api/communities/' + slug + '/dashboard'));
        } catch (e) { setErr((e as Error).message); }
    }, [api, authed, slug]);
    useEffect(() => { load(); }, [load]);

    if (!authed) return (
        <div className="prelogin">
            <p>Log in to see the community dashboard.</p>
            <button type="button" className="plain" onClick={login}>Log in</button>
        </div>
    );
    if (err) return (
        <main className="dashboard-wrap"><p style={{ color: '#8b1919' }}>{err}</p></main>
    );
    if (!stats) return <div style={{ padding: 40, textAlign: 'center', color: '#666' }}>Loading…</div>;

    return (
        <main>
            <div className="dashboard-wrap">
                <div className="masthead">
                    <div className="rule-top" />
                    <h1>{stats.community_slug}</h1>
                    <div className="issue">Community dashboard · last 30 days</div>
                    <div className="rule-bottom" />
                </div>

                <nav className="dash-tabs">
                    <Link to={`/communities/${slug}`}           className="dash-tab" style={{ textDecoration: 'none' }}>← Read paper</Link>
                    <Link to={`/communities/${slug}/newsroom`}  className="dash-tab" style={{ textDecoration: 'none' }}>Newsroom{stats.pending_count > 0 ? ` (${stats.pending_count})` : ''}</Link>
                    <Link to={`/communities/${slug}/settings`}  className="dash-tab" style={{ textDecoration: 'none' }}>Settings</Link>
                    <span className="dash-tab is-active" style={{ cursor: 'default' }}>Dashboard</span>
                </nav>

                <Overview stats={stats} />
            </div>
        </main>
    );
}

// -------- Overview --------
function Overview({ stats }: { stats: CommunityDashboardStats }) {
    return (
        <>
            <section className="stat-grid">
                <StatCard label="Editors"             total={stats.moderator_count} delta={0} />
                <StatCard label="Approved posts"      total={stats.approved_total} delta={stats.approved_30d} />
                <StatCard label="Likes"               total={stats.total_likes} delta={stats.likes_30d} />
                <StatCard label="Comments"            total={stats.total_comments} delta={stats.comments_30d} />
            </section>
            {stats.open_flag_count > 0 && (
                <div className="dash-alert">⚠ {stats.open_flag_count} open flag{stats.open_flag_count === 1 ? '' : 's'}</div>
            )}
            <section className="dashboard-block">
                <h2>Approved posts per week</h2>
                <BarChart data={stats.approved_per_week} />
            </section>
            <section className="dashboard-block">
                <h2>Likes per week</h2>
                <BarChart data={stats.likes_per_week} accent />
            </section>
            <section className="dashboard-block">
                <h2>Top posts</h2>
                {stats.top_posts.length === 0 ? (
                    <p className="empty">No approved posts yet.</p>
                ) : (
                    <table className="dashboard-table">
                        <thead><tr><th>Post</th><th>Kind</th><th>Recs</th><th>Comments</th><th>Published</th></tr></thead>
                        <tbody>
                            {stats.top_posts.map(p => (
                                <tr key={p.post_id}>
                                    <td><Link to={'/posts/' + p.post_id}>{p.title || '(untitled)'}</Link></td>
                                    <td className="dashboard-kind">{p.post_type}</td>
                                    <td>{p.like_count}</td>
                                    <td>{p.comment_count}</td>
                                    <td>{timeAgo(p.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </section>
        </>
    );
}


// -------- shared bits (mirror GroupDashboard so both feel like siblings) --------
function StatCard({ label, total, delta }: { label: string; total: number; delta: number }) {
    return (
        <div className="stat-card">
            <div className="stat-total">{total}</div>
            <div className="stat-label">{label}</div>
            {delta > 0 && <div className="stat-delta">+{delta} in last 30d</div>}
        </div>
    );
}

function BarChart({ data, accent }: { data: Array<{ week_start: string; count: number }>; accent?: boolean }) {
    const max = Math.max(1, ...data.map(d => d.count));
    return (
        <div className="bar-chart">
            {data.map(d => {
                const pct = (d.count / max) * 100;
                const date = new Date(d.week_start);
                return (
                    <div className="bar-col" key={d.week_start}>
                        <div className="bar-fill-wrap">
                            <div className={'bar-fill' + (accent ? ' accent' : '')}
                                 style={{ height: pct + '%' }}
                                 title={`${d.count} in week of ${date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}`} />
                        </div>
                        <div className="bar-count">{d.count}</div>
                        <div className="bar-label">{date.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short' })}</div>
                    </div>
                );
            })}
        </div>
    );
}
